import { Filter, Layers, Building2 } from 'lucide-react';
import { ViewLevel } from '../../contexts/ViewContext';
import { NODE_TYPE_LABELS } from '../orgchart/types';
import type { SavedDashboardFilter } from './types';

interface DashboardHeaderProps {
  savedFilters: SavedDashboardFilter[];
  activeFilterPresetId: number | null;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  onLoadPreset: (preset: SavedDashboardFilter, skipFetch?: boolean) => void;
  onRefresh: () => void;
  // CR-3834: Export report functionality
  onExportReport?: () => void;
  exporting?: boolean;
  // CR-3384: View level info for display
  viewLevel?: ViewLevel;
  selectedGroupId?: number | null;
  viewLevelName?: string;
}

export function DashboardHeader({
  savedFilters,
  activeFilterPresetId,
  showFilters,
  setShowFilters,
  onLoadPreset,
  onRefresh,
  onExportReport,
  exporting,
  viewLevel,
  selectedGroupId,
  viewLevelName,
}: DashboardHeaderProps) {
  // CR-3384: Get view level display info (uses configurable hierarchy labels)
  // selectedGroupId === 0 = synthetic root group, > 0 = division sub-group
  const getViewLevelDisplay = () => {
    if (!viewLevel) return null;

    switch (viewLevel) {
      case 'GROUP':
        // Distinguish root group (id=0) from division sub-groups (id>0)
        if (selectedGroupId && selectedGroupId > 0) {
          return {
            icon: <Building2 className="h-4 w-4" />,
            label: NODE_TYPE_LABELS['division'],
            name: viewLevelName || 'All Companies',
            color: 'text-indigo-600 bg-indigo-100',
          };
        }
        return {
          icon: <Layers className="h-4 w-4" />,
          label: NODE_TYPE_LABELS['group'],
          name: viewLevelName || 'All Companies',
          color: 'text-purple-600 bg-purple-100',
        };
      case 'SITE':
        return {
          icon: <Building2 className="h-4 w-4" />,
          label: NODE_TYPE_LABELS['company'],
          name: viewLevelName || 'All Companies',
          color: 'text-blue-600 bg-blue-100',
        };
      case 'COMPANY':
      default:
        return {
          icon: <Building2 className="h-4 w-4" />,
          label: NODE_TYPE_LABELS['company'],
          name: viewLevelName || NODE_TYPE_LABELS['company'],
          color: 'text-blue-600 bg-blue-100',
        };
    }
  };

  const viewDisplay = getViewLevelDisplay();

  return (
    <div className="px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold">Dashboard</h1>
          {/* CR-3384: View level indicator */}
          {viewDisplay && (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${viewDisplay.color}`}>
              {viewDisplay.icon}
              <span>{viewDisplay.label}:</span>
              <span className="font-semibold">{viewDisplay.name}</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          {/* Load Saved Filter Dropdown */}
          {savedFilters.length > 0 && (
            <select
              value={activeFilterPresetId?.toString() || ''}
              onChange={(e) => {
                const preset = savedFilters.find((f) => f.id.toString() === e.target.value);
                if (preset) onLoadPreset(preset);
              }}
              className="px-4 py-2 border border-border rounded-lg hover:bg-muted bg-background"
            >
              <option value="" disabled>Load Saved Filter...</option>
              {savedFilters.map((filter) => (
                <option key={filter.id} value={filter.id}>
                  {filter.is_default && '⭐ '}{filter.name}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border rounded-lg flex items-center gap-2 ${
              showFilters
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border hover:bg-muted'
            }`}
          >
            <Filter className="h-4 w-4" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button
            onClick={onRefresh}
            className="px-4 py-2 border border-border rounded-lg hover:bg-muted"
          >
            Refresh
          </button>
          {/* CR-3834: Export Report button with handler */}
          <button
            onClick={onExportReport}
            disabled={exporting}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? 'Exporting...' : 'Export Report'}
          </button>
        </div>
      </div>
    </div>
  );
}
